// Mirrors internal/kinds/learn (concept.go, outline.go).
import type { Resource } from '@/db/types'
import { compact, conflict, invalid } from '@/kinds/kind'
import { formatTime } from '@/lib/time'
import {
  FAMILIARITIES,
  type ConceptSpec,
  type ConceptStatus,
  type CourseSpec,
  type CourseStatus,
  type Familiarity,
  type LessonSpec,
  type LessonStatus,
  type ModuleSpec,
  type ExerciseSpec,
  type ExerciseStatus,
  type ModuleStatus,
  type Result,
  type SessionSpec,
  type SessionStatus,
} from './schema'

export type ConceptResource = Resource<ConceptSpec, ConceptStatus>
export type CourseResource = Resource<CourseSpec, CourseStatus>
export type ModuleResource = Resource<ModuleSpec, ModuleStatus>
export type LessonResource = Resource<LessonSpec, LessonStatus>
export type SessionResource = Resource<SessionSpec, SessionStatus>
export type ExerciseResource = Resource<ExerciseSpec, ExerciseStatus>

export const titled = <S extends { title: string }>(r: { spec: S }) => r.spec.title
export const withTitle =
  <S extends { title: string }>() =>
  (spec: Partial<S>): S =>
    ({ title: '', ...spec }) as S

export const courseInitialStatus = (): CourseStatus => ({ state: 'active' })
export const moduleInitialStatus = (): ModuleStatus => ({ state: 'active' })
export const lessonInitialStatus = (): LessonStatus => ({ state: 'todo' })

/** Course and Module have no actions: their progress is rolled up by the server's reconciler. */
export const noActions = <R>(_r: R, action: string): R => {
  throw invalid(`unknown action ${action}`)
}

export const lessonApply = (r: LessonResource, action: string, args: unknown, at: string): LessonResource => {
  const st = r.status
  switch (action) {
    case 'mark_done': {
      if (st.state === 'done') throw conflict('lesson is already done')
      const minutes = (args as { actual_minutes?: unknown } | null)?.actual_minutes
      const actual = typeof minutes === 'number' ? Math.trunc(minutes) : 0
      if (actual < 0) throw invalid('mark_done: actual_minutes cannot be negative')
      return {
        ...r,
        status: compact({
          state: 'done',
          completed_at: formatTime(new Date(at)),
          actual_minutes: actual || undefined,
        }),
      }
    }
    case 'skip':
      if (st.state !== 'todo') throw conflict(`lesson is ${st.state}`)
      return { ...r, status: { state: 'skipped' } }
    case 'reopen':
      if (st.state === 'todo') throw conflict('lesson is already todo')
      return { ...r, status: { state: 'todo' } }
    default:
      throw invalid(`unknown action ${action}`)
  }
}

export const conceptDefaults = (spec: Partial<ConceptSpec>): ConceptSpec => ({ name: '', ...spec })
export const conceptInitialStatus = (r: ConceptResource): ConceptStatus => ({
  familiarity: 'new',
  last_touched: formatTime(new Date(r.created_at)),
})
export const conceptName = (r: ConceptResource) => r.spec.name

export const conceptAfterPatch = (r: ConceptResource, at: string): ConceptResource => ({
  ...r,
  status: compact({ ...r.status, last_touched: formatTime(new Date(at)) }),
})

export const conceptApply = (
  r: ConceptResource,
  action: string,
  args: unknown,
  at: string,
): ConceptResource => {
  if (action !== 'set_familiarity') throw invalid(`unknown action ${action}`)
  const state = (args as { state?: unknown } | null)?.state
  if (!FAMILIARITIES.includes(state as Familiarity))
    throw invalid('set_familiarity: state must be new, learning or known')
  if (r.status.familiarity === state) throw conflict(`concept is already ${r.status.familiarity}`)
  return {
    ...r,
    status: compact({
      ...r.status,
      familiarity: state as Familiarity,
      last_touched: formatTime(new Date(at)),
    }),
  }
}

export const sessionDefaults = (spec: Partial<SessionSpec>): SessionSpec => ({
  started_at: new Date().toISOString(),
  mode: 'lesson',
  ...spec,
})
export const sessionInitialStatus = (): SessionStatus => ({})
export const sessionName = (r: SessionResource) => `${r.spec.mode} ${r.spec.started_at}`

const resultArg = (args: unknown): Result | undefined => {
  const res = (args as { result?: unknown } | null)?.result
  if (res === undefined || res === '') return undefined
  if (res !== 'right' && res !== 'partial' && res !== 'wrong')
    throw invalid('result must be right, partial or wrong')
  return res
}

const bump = (st: SessionStatus, res: Result | undefined, by: number): SessionStatus => {
  if (!res) return st
  const n = (st[res] ?? 0) + by
  return n < 0 ? st : { ...st, [res]: n || undefined }
}

export const sessionApply = (
  r: SessionResource,
  action: string,
  args: unknown,
  at: string,
): SessionResource => {
  const st = r.status
  switch (action) {
    case 'item_done': {
      if (st.ended_at) throw conflict('session has ended')
      const res = resultArg(args)
      return { ...r, status: compact(bump({ ...st, items_done: (st.items_done ?? 0) + 1 }, res, 1)) }
    }
    case 'item_undone': {
      if (!st.items_done) throw conflict('no item to take back')
      const res = resultArg(args)
      return { ...r, status: compact(bump({ ...st, items_done: st.items_done - 1 || undefined }, res, -1)) }
    }
    case 'end': {
      if (st.ended_at) throw conflict('session has already ended')
      const given = (args as { actual_minutes?: unknown } | null)?.actual_minutes
      let mins = 0
      if (typeof given === 'number') {
        mins = Math.trunc(given)
        if (mins < 0) throw invalid('end: actual_minutes cannot be negative')
      } else {
        const gap = new Date(at).getTime() - new Date(r.spec.started_at).getTime()
        if (gap > 0) mins = Math.round(gap / 60000)
      }
      return {
        ...r,
        status: compact({ ...st, ended_at: formatTime(new Date(at)), actual_minutes: mins || undefined }),
      }
    }
    case 'resume':
      if (!st.ended_at) throw conflict('session is still open')
      return {
        ...r,
        status: compact({ ...st, ended_at: undefined, actual_minutes: undefined }),
      }
    default:
      throw invalid(`unknown action ${action}`)
  }
}

export const exerciseInitialStatus = (): ExerciseStatus => ({ state: 'open' })
export const exerciseDefaults = (spec: Partial<ExerciseSpec>): ExerciseSpec => ({
  format: 'learn',
  prompt: '',
  ...spec,
})
export const exerciseName = (r: ExerciseResource) => r.spec.prompt

/**
 * How a pick of tiles fares: right when it is exactly the correct ones, wrong when none of the picks
 * is, partial when mixed. Mirrors `Grade` in Go.
 */
export const grade = (choices: { correct?: boolean }[], picked: number[]): Result => {
  const correct = choices.filter((c) => c.correct).length
  const right = picked.filter((i) => choices[i]?.correct).length
  const wrong = picked.length - right
  if (right === correct && wrong === 0) return 'right'
  return right === 0 ? 'wrong' : 'partial'
}

export const exerciseApply = (
  r: ExerciseResource,
  action: string,
  args: unknown,
  at: string,
): ExerciseResource => {
  const a = (args ?? {}) as { picked?: unknown; got?: unknown; result?: unknown; answered_at?: unknown }
  const stamp = formatTime(new Date(at))
  switch (action) {
    case 'answer': {
      if (r.spec.format === 'learn') {
        if (typeof a.got !== 'boolean') throw invalid('answer: a Learn card is answered with got')
        return {
          ...r,
          status: { state: 'answered', result: a.got ? 'right' : 'partial', answered_at: stamp },
        }
      }
      const picked = Array.isArray(a.picked) ? (a.picked as unknown[]) : []
      if (!picked.length) throw invalid('answer: picked needs at least one choice')
      if (picked.length > 1 && !r.spec.multi_select) throw invalid('answer: this question takes one choice')
      const choices = r.spec.choices ?? []
      const seen = new Set<number>()
      for (const i of picked) {
        if (
          !Number.isInteger(i) ||
          (i as number) < 0 ||
          (i as number) >= choices.length ||
          seen.has(i as number)
        )
          throw invalid('answer: picked is out of range or repeated')
        seen.add(i as number)
      }
      return {
        ...r,
        status: { state: 'answered', result: grade(choices, picked as number[]), answered_at: stamp },
      }
    }
    case 'reset': {
      if (!a.result) return { ...r, status: { state: 'open' } }
      if (a.result !== 'right' && a.result !== 'partial' && a.result !== 'wrong')
        throw invalid('reset: result must be right, partial or wrong')
      return {
        ...r,
        status: compact({
          state: 'answered',
          result: a.result,
          answered_at: typeof a.answered_at === 'string' ? a.answered_at : undefined,
        }),
      }
    }
    default:
      throw invalid(`unknown action ${action}`)
  }
}
